import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  Transition,
  unref,
  vShow,
  watch,
  withDirectives
} from 'vue';

import { CdsTransitionCollapse } from '@components/transitions';
import { useRender } from '@components/composable';

import CdsTreeNode from '../CdsTreeNode.vue';
import { makeTreeNodeProps } from '../composable';

import type { NTree } from '../interface';
import type { PropType, ComputedRef } from 'vue';

export default defineComponent({
  name: 'MotionTreeNode',
  inheritAttrs: false,
  props: {
    ...makeTreeNodeProps(),

    active: Boolean,
    motion: Object,
    motionNodes: { type: Array as PropType<NTree.IFlattenNode[] | null> },
    onMotionStart: Function,
    onMotionEnd: Function,
    motionType: String as PropType<'show' | 'hide' | null>
  },
  setup(props, { attrs, slots }) {
    const visible = ref(true);
    const motionedRef = ref(false);
    const transitionProps: ComputedRef<Record<string, any>> = computed(() => {
      if (props.motion) {
        return props.motion;
      } else {
        return CdsTransitionCollapse();
      }
    });

    const onMotionEnd = (node?: Element, type?: 'appear' | 'leave') => {
      if (type === 'appear') {
        unref(transitionProps)?.onAfterEnter?.(node);
      } else if (type === 'leave') {
        unref(transitionProps)?.onAfterLeave?.(node);
      }
      if (!unref(motionedRef)) {
        props.onMotionEnd && props.onMotionEnd();
      }
      motionedRef.value = true;
    };

    watch(
      () => props.motionNodes,
      () => {
        if (props.motionNodes && props.motionType === 'hide' && visible.value) {
          nextTick(() => {
            visible.value = false;
          });
        }
      },
      { immediate: true, flush: 'post' }
    );
    onMounted(() => {
      props.motionNodes && props.onMotionStart && props.onMotionStart();
    });
    onBeforeUnmount(() => {
      props.motionNodes && onMotionEnd();
    });

    useRender(() => {
      const { motionNodes, motionType, active, eventKey, ...otherProps } = props;

      if (motionNodes) {
        return h(
          Transition,
          {
            ...unref(transitionProps),
            appear: motionType === 'show',
            onAfterAppear: (node) => onMotionEnd(node, 'appear'),
            onAfterLeave: (node) => onMotionEnd(node, 'leave')
          },
          {
            default: () =>
              withDirectives(
                h(
                  'div',
                  { class: 'cds-treenode__motion' },
                  motionNodes.map((treeNode) => {
                    const {
                      data: { ...restProps },
                      title,
                      key,
                      isStart,
                      isEnd
                    } = treeNode;

                    delete restProps.children;

                    return h(
                      CdsTreeNode,
                      { ...restProps, title, active, data: treeNode.data, key, eventKey: key, isStart, isEnd },
                      slots
                    );
                  })
                ),
                [[vShow, unref(visible)]]
              )
          }
        );
      }

      return h(
        CdsTreeNode,
        {
          class: attrs.class,
          style: attrs.style,
          ...otherProps,
          active,
          eventKey
        },
        slots
      );
    });
  }
});
